/**
 * index.tsx
 *
 * This is the entry file for the application, only setup and boilerplate
 * code.
 */

import 'react-app-polyfill/ie11';
import 'react-app-polyfill/stable';
import { SnackbarProvider } from 'notistack';
import * as React from 'react';
import * as ReactDOMClient from 'react-dom/client';
import { throttle } from 'lodash';
import { Provider } from 'react-redux';
import FontFaceObserver from 'fontfaceobserver';
import * as serviceWorker from 'serviceWorker';
import { ThemeProvider as MuiThemeProvider } from '@mui/material';
import 'sanitize.css/sanitize.css';
// Initialize languages
import { App } from 'app';
import { HelmetProvider } from 'react-helmet-async';
import { configureAppStore } from 'store/configureStore';
import { saveState } from './store/localStorage';
import { AuthProvider } from './app/providers/AuthProvider';
import { muiTheme } from './styles/theme/themes';
import { PusherProvider } from './app/containers/PusherProvider';
import { ConfirmProvider } from './app/components/ConfirmDialog';
import { LoggingProvider } from 'app/providers/LoggingProvider';
import {
  createBrowserRouter,
  createRoutesFromElements,
  Route,
  RouterProvider,
} from 'react-router-dom';
import { GlobalStyles } from './styles/GlobalStyles';
import { LicenseInfo } from '@mui/x-data-grid-pro';

declare global {
  interface Window {
    Beamer?: {
      notificationNumber: number;
      update: (params: { [key: string]: any }) => void;
    };
  }
}

// Observe loading of Inter (to remove 'Inter', remove the <link> tag in
// the index.html file and this observer)
const openSansObserver = new FontFaceObserver('Fira Sans', {});
const headerObserver = new FontFaceObserver('Montserrat', {});

// When Inter is loaded, add a font-family using Inter to the body
Promise.all([openSansObserver.load(), headerObserver.load()]).then(() => {
  document.body.classList.add('fontLoaded');
});

const store = configureAppStore();

// todo - might need to use this https://edvins.io/how-to-use-redux-persist-with-redux-toolkit
// instead of the loadState idea in the CustomerSelector slice that has been used
store.subscribe(
  throttle(() => {
    const state = store.getState();
    saveState({
      customerSelector: {
        currentCustomers: (state?.customerSelector || {}).currentCustomers,
      },
    });
  }, 1500),
);

const MOUNT_NODE = document.getElementById('root') as HTMLElement;

const router = createBrowserRouter(
  createRoutesFromElements(<Route path={'*'} element={<App />} />),
);

// set License key for DataGrid from env

const licenseKey = process.env.REACT_APP_MUI_X_LICENSE_KEY;

LicenseInfo.setLicenseKey(licenseKey || 'PRO-TEAM-DEMO-KEY');

ReactDOMClient.createRoot(MOUNT_NODE!).render(
  <Provider store={store}>
    <MuiThemeProvider theme={muiTheme}>
      <ConfirmProvider>
        <GlobalStyles />
        <AuthProvider>
          <SnackbarProvider>
            <PusherProvider>
              <HelmetProvider>
                <React.StrictMode>
                  <RouterProvider router={router} />
                </React.StrictMode>
              </HelmetProvider>
            </PusherProvider>
          </SnackbarProvider>
        </AuthProvider>
      </ConfirmProvider>
    </MuiThemeProvider>
    <LoggingProvider />
  </Provider>,
);

if (module.hot) {
  // Hot reloadable translation json files and app
  // modules.hot.accept does not accept dynamic dependencies,
  // have to be constants at compile-time
}

// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: https://bit.ly/CRA-PWA
serviceWorker.unregister();
